<template>
<div class="dashboard-toolbar">
    <div class="dt-tabs" role="tablist">
        <button
            type="button"
            v-for="t in visibleTabs"
            :key="t.key"
            class="dt-tab"
            :class="{ active: tab === t.key }"
            role="tab"
            :aria-selected="tab === t.key ? 'true' : 'false'"
            @click="$emit('tabChange', t.key)"
        >{{ t.label }}</button>
    </div>

    <!-- The create / upgrade CTA lives at the top of the right rail
         (views/dashboard.vue), not here: centred in a full-width toolbar it sat
         over the gutter between the two columns below and aligned to neither. -->
    <div class="dt-right">
        <label class="dt-sort" v-if="viewMode === 'grid'">
            <span class="dt-sort-label">Sort:</span>
            <select class="md-select md-theme-default" :value="sortValue" @change="onSortSelect($event.target.value)">
                <option class="md-option" value="date-desc">Date (newest to oldest)</option>
                <option class="md-option" value="date-asc">Date (oldest to newest)</option>
                <option class="md-option" value="name-asc">Name (A to Z)</option>
                <option class="md-option" value="name-desc">Name (Z to A)</option>
            </select>
        </label>
        <span class="dt-view">
            <span class="dt-view-label">View:</span>
            <button
                type="button"
                class="dt-view-btn"
                :class="{ active: viewMode === 'list' }"
                :aria-pressed="viewMode === 'list' ? 'true' : 'false'"
                aria-label="List view"
                @click="$emit('viewChange', 'list')"
            ><md-icon>view_list</md-icon><md-tooltip md-direction="bottom">List view</md-tooltip></button>
            <button
                type="button"
                class="dt-view-btn"
                :class="{ active: viewMode === 'grid' }"
                :aria-pressed="viewMode === 'grid' ? 'true' : 'false'"
                aria-label="Thumbnail view"
                @click="$emit('viewChange', 'grid')"
            ><md-icon>view_module</md-icon><md-tooltip md-direction="bottom">Thumbnail view</md-tooltip></button>
        </span>
    </div>
</div>
</template>

<script>
/**
 * Dashboard control strip: tabs (My / All, plus Shared with Me only when the
 * user has shared projects), a single shared sort control (grid view; list
 * view sorts via column headers bound to the same model), and the view toggle.
 * The create / upgrade CTA now lives at the top of the right rail in
 * views/dashboard.vue. Presentational — emits events only.
 */
const DashboardToolbar = {
    name: 'DashboardToolbar',
    props: {
        tab: { type: String, default: 'my' },
        viewMode: { type: String, default: 'grid' },
        sort: { type: Object, default: () => ({ key: 'date', dir: 'desc' }) },
        hasShared: { type: Boolean, default: false }
    },
    computed: {
        visibleTabs() {
            const tabs = [{ key: 'my', label: 'My Projects' }];
            if (this.hasShared) tabs.push({ key: 'shared', label: 'Shared with Me' });
            tabs.push({ key: 'all', label: 'All Projects' });
            return tabs;
        },
        sortValue() {
            return `${this.sort.key}-${this.sort.dir}`;
        }
    },
    methods: {
        onSortSelect(value) {
            const [key, dir] = value.split('-');
            this.$emit('sortChange', { key, dir });
        }
    }
};
export default DashboardToolbar;
</script>

<style lang="scss">
.dashboard-toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    // No top margin: the toolbar is the first child of .dash-main and has to
    // start flush with the column top so it lines up with the create CTA at the
    // top of the right rail. Breathing room above comes from .dash-columns.
    margin: 0 0 16px;

    .dt-tabs {
        display: inline-flex;
        border: 1px solid #ccc;
        border-radius: 6px;
        overflow: hidden;
        // Pushes the controls to the right edge (replaces the old .dt-center
        // spacer that used to hold the create CTA).
        margin-right: auto;
        .dt-tab {
            background: #fff;
            border: none;
            padding: 8px 16px;
            cursor: pointer;
            font-size: 14px;
            color: #333;
            border-right: 1px solid #eee;
            &:last-child { border-right: none; }
            /* Dark ink on teal, never white. White on the brand teal is 1.70:1,
               far under the 4.5:1 WCAG AA minimum; #05323b is 8.10:1. Dark mode
               already did this — light mode was the outlier. */
            &.active { background: var(--accent); color: #05323b; font-weight: 600; }
            &:focus { outline: 2px solid var(--accent); outline-offset: -2px; }
        }
    }
    .dt-right { display: inline-flex; align-items: center; gap: 16px; }

    .dt-sort {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        .dt-sort-label { color: #666; }
        select { padding: 4px 6px; border-radius: 4px; }
    }
    .dt-view {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        .dt-view-label { color: #666; font-size: 13px; margin-right: 4px; }
        .dt-view-btn {
            background: #fff;
            border: 1px solid #ccc;
            padding: 4px 8px;
            cursor: pointer;
            display: inline-flex;
            i { color: #888; }
            /* Same pairing rule as the tabs above — teal takes dark ink. */
            &.active { background: var(--accent); i { color: #05323b; } }
            &:first-of-type { border-radius: 4px 0 0 4px; }
            &:last-of-type { border-radius: 0 4px 4px 0; border-left: none; }
            &:focus { outline: 2px solid var(--accent); outline-offset: -1px; }
        }
    }
}
.md-theme-show-mode {
    .dashboard-toolbar {
        .dt-tabs { border-color: #546e7a; .dt-tab { background: #37474f; color: rgba(255,255,255,0.85); border-right-color: #455a64; &.active { background: var(--accent); color: #05323b; } } }
        .dt-sort .dt-sort-label, .dt-view .dt-view-label { color: rgba(255,255,255,0.7); }
        .dt-view .dt-view-btn { background: #37474f; border-color: #546e7a; i { color: #b0bec5; } &.active { background: var(--accent); i { color: #05323b; } } }
    }
}
</style>


// WEBPACK FOOTER //
// dashboard-toolbar.vue?46050152